import React, { useEffect, useMemo, useRef, useState } from "react";
import { Outlet, useLocation } from "react-router-dom";
import Sidebar from "./Sidebar";
import Header from "./Header";
import useMediaQuery from "../hooks/useMediaQuery";
import usePrefersReducedMotion from "../hooks/usePrefersReducedMotion";

const NAV_ITEMS = [
  {
    id: "rom-pad",
    label: "ROM Pad",
    path: "/rom-pad",
    icon: "rom",
  },
  {
    id: "block-table",
    label: "Block Table",
    path: "/block-table",
    icon: "blocks",
  },
  {
    id: "account-settings",
    label: "Account Settings",
    path: "/account-settings",
    icon: "settings",
  },
];

const PAGE_TITLES = NAV_ITEMS.reduce((acc, item) => {
  acc[item.path] = item.label;
  return acc;
}, {});

function Layout() {
  const location = useLocation();
  const isMobile = useMediaQuery("(max-width: 960px)");
  const [isSidebarOpen, setIsSidebarOpen] = useState(() => !isMobile);
  const panelRef = useRef(null);
  const prefersReducedMotion = usePrefersReducedMotion();
  const [sidebarTransitionState, setSidebarTransitionState] = useState("idle");

  useEffect(() => {
    setIsSidebarOpen(!isMobile);
  }, [isMobile]);

  useEffect(() => {
    const panel = panelRef.current;
    if (!panel) {
      return undefined;
    }

    if (prefersReducedMotion) {
      panel.classList.remove("page-panel--transitioning");
      panel.classList.add("page-panel--visible");
      panel.focus({ preventScroll: true });
      return undefined;
    }

    panel.classList.remove("page-panel--visible");
    panel.classList.add("page-panel--transitioning");
    // Force reflow so that the class removal is registered before re-adding it,
    // which allows consecutive transitions to play reliably.
    // eslint-disable-next-line no-unused-expressions
    panel.offsetWidth;

    const frame = requestAnimationFrame(() => {
      panel.classList.add("page-panel--visible");
      panel.focus({ preventScroll: true });
    });

    const handleTransitionEnd = (event) => {
      if (event.target === panel && event.propertyName === "opacity") {
        panel.classList.remove("page-panel--transitioning");
      }
    };

    panel.addEventListener("transitionend", handleTransitionEnd);

    return () => {
      cancelAnimationFrame(frame);
      panel.removeEventListener("transitionend", handleTransitionEnd);
    };
  }, [location.pathname, prefersReducedMotion]);

  useEffect(() => {
    if (prefersReducedMotion || isMobile) {
      setSidebarTransitionState("idle");
      return undefined;
    }

    setSidebarTransitionState(isSidebarOpen ? "opening" : "closing");

    const timeout = setTimeout(() => {
      setSidebarTransitionState("idle");
    }, 360);

    return () => clearTimeout(timeout);
  }, [isSidebarOpen, prefersReducedMotion, isMobile]);

  const pageTitle = useMemo(() => {
    return PAGE_TITLES[location.pathname] || "Kropz Dashboard";
  }, [location.pathname]);

  const handleToggleSidebar = () => {
    setIsSidebarOpen((current) => !current);
  };

  const handleNavigate = () => {
    if (isMobile) {
      setIsSidebarOpen(false);
    }
  };

  return (
    <div
      className={`app-shell ${isSidebarOpen ? "sidebar-open" : "sidebar-closed"} ${
        isMobile ? "is-mobile" : ""
      }`}
      data-sidebar-transition={sidebarTransitionState}
    >
      <Sidebar
        items={NAV_ITEMS}
        isOpen={isSidebarOpen}
        onToggle={handleToggleSidebar}
        onNavigate={handleNavigate}
        isMobile={isMobile}
      />
      {isMobile && isSidebarOpen ? (
        <button
          type="button"
          className="sidebar-overlay"
          aria-label="Close navigation"
          onClick={handleNavigate}
        />
      ) : null}
      <div className="app-shell__content">
        <Header
          onToggleSidebar={handleToggleSidebar}
          isMobile={isMobile}
          isSidebarOpen={isSidebarOpen}
        />
        <main className="app-shell__main" aria-live="polite">
          <section
            key={location.pathname}
            className="page-panel"
            tabIndex={-1}
            ref={panelRef}
          >
            <div className="page-heading">
              <div className="page-heading__primary">
                <p className="page-eyebrow">Operations</p>
                <h1>{pageTitle}</h1>
              </div>
              <div className="page-heading__actions" data-page-heading-actions />
            </div>
            <Outlet />
          </section>
        </main>
      </div>
    </div>
  );
}

export default Layout;
